import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import DateRangePicker from "../DateRangePicker ";
import { getApprovedRfqs, searchRfqWithDate } from "actions/rfqActions";
import {
  formatDateRangeLabel,
  getMatchingDatePreset,
  getPresetDateRange,
} from "utils/dateFilterUtils";

const FilterSection = ({
  handleChangeTeamMemberNew,
  workspaceView,
  rfqsData,
  handleGetRfqs,
  setRfqFilterStatus,
  user,
  projectId = "",
  rfqFilterStatus,
  setSelectedRfqTypes,
  selectedRfqTypes,
  getWorkspaceType,
  teamUserIdsInString,
  businessDivisionOptions = [],
  selectedBusinessDivisions,
  setSelectedBusinessDivisions,
  selectedProjectNames = [],
  setSelectedProjectNames,
  selectedCategoryFilter,
  setSelectedCategoryFilter,
  onClose,
}) => {
  const dispatch = useDispatch();


  const [selectedStatus, setSelectedStatus] = useState(rfqFilterStatus);
  const [openKeys, setOpenKeys] = useState({
    dateRange: true,
    status: true,
    team: true,
    rfqType: true,
    businessDivision: true,
    project: true,
    category: true,
  });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedCreatedDateOption, setSelectedCreatedDateOption] = useState("");
  const [customCreatedStartDate, setCustomCreatedStartDate] = useState("");
  const [customCreatedEndDate, setCustomCreatedEndDate] = useState("");
  const [createdDateError, setCreatedDateError] = useState("");
  const [dateFilterName, setDateFilterName] = useState("");
  const [closeDateFilterName, setCloseDateFilterName] = useState("");
  const [teamCreatorWiseCounts, setTeamCreatorWiseCounts] = useState([]);
  const [filterApplied, setFilterApplied] = useState(false);
  const [allDataClr, setAllDataClr] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [expandedCategoryDisciplines, setExpandedCategoryDisciplines] = useState({});

  useEffect(() => {
    setSelectedStatus(rfqFilterStatus);
  }, [rfqFilterStatus]);

  const filterColData = [
    "DRAFT",
    "OPEN",
    "REWORK",
    "PENDING APPROVAL",
    "CLOSED",
    "DELETED",
  ];

  // RFQ Types - already in lowercase to match your database
  const rfqTypes = [
    { value: "material", label: "Material" },
    { value: "labour", label: "Labour" },
    { value: "contractor", label: "Contractor" }
  ];

  const createdDateOptions = [
    { value: "today", label: "Today" },
    { value: "last7Days", label: "Last 7 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "thisQuarter", label: "This Quarter" },
    { value: "last6Months", label: "Last 6 Months" },
    { value: "custom", label: "Custom" },
  ];

  const entityId = user?.userEntity?.entityId;
  const statusWiseCounts = rfqsData?.statusWiseCounts;
  const rfqTypeWiseCounts = rfqsData?.rfqTypeWiseCounts;
  const businessDivisionWiseCounts = rfqsData?.businessDivisionWiseCounts;
  const creatorWiseCounts = rfqsData?.creatorWiseCounts;
  const projectWiseCounts = rfqsData?.projectWiseCounts || [];
  const categoryFilterOptions = rfqsData?.categoryFilterOptions || [];
  const totalCount = rfqsData?.totalCount;
  const teamUserIds = user?.teamMembers?.map((member) => member.userId) || [];
  const creatorWiseCountsForDisplay =
    workspaceView === "Team Workspace" &&
    selectedUserIds?.length > 0 &&
    teamCreatorWiseCounts?.length > 0
      ? teamCreatorWiseCounts
      : creatorWiseCounts;

  // convert statusWiseCounts -> map for quick lookups
  const convertedData = statusWiseCounts?.reduce((acc, curr) => {
    acc[curr._id] = curr?.count;
    return acc;
  }, {}) || {};

  const rfqTypeCountsMap = rfqTypeWiseCounts?.reduce((acc, curr) => {
    if (curr?._id) {
      acc[curr._id] = curr?.count ?? 0;
    }
    return acc;
  }, {}) || {};

  const businessDivisionCountsMap = businessDivisionWiseCounts?.reduce((acc, curr) => {
    if (curr?._id) {
      acc[curr._id] = curr?.count ?? 0;
    }
    return acc;
  }, {}) || {};

  const creatorCountsMap = creatorWiseCountsForDisplay?.reduce((acc, curr) => {
    if (curr?._id !== undefined && curr?._id !== null) {
      acc[String(curr._id)] = curr?.count ?? 0;
    }
    return acc;
  }, {}) || {};
  const selectedProjectNameList = Array.isArray(selectedProjectNames)
    ? selectedProjectNames
    : [];
  const selectedCategoryFilterKey = [
    selectedCategoryFilter?.rfqType,
    selectedCategoryFilter?.disciplineId,
    selectedCategoryFilter?.categoryId,
    selectedCategoryFilter?.subCategoryId,
  ].filter(Boolean).join("-");
  const selectedCategoryFilterLooseKey = [
    selectedCategoryFilter?.disciplineId,
    selectedCategoryFilter?.categoryId,
    selectedCategoryFilter?.subCategoryId,
  ].filter(Boolean).join("-");
  const getCategoryOptionKey = (option) => [
    option?.categoryFilter?.rfqType || option?.rfqType,
    option?.categoryFilter?.disciplineId,
    option?.categoryFilter?.categoryId,
    option?.categoryFilter?.subCategoryId,
  ].filter(Boolean).join("-");
  const getCategoryOptionLooseKey = (option) => [
    option?.categoryFilter?.disciplineId,
    option?.categoryFilter?.categoryId,
    option?.categoryFilter?.subCategoryId,
  ].filter(Boolean).join("-");
  const isCategoryOptionSelected = (option) => {
    const optionKey = getCategoryOptionKey(option);
    const optionLooseKey = getCategoryOptionLooseKey(option);

    return !!(
      (optionKey && optionKey === selectedCategoryFilterKey) ||
      (optionLooseKey && optionLooseKey === selectedCategoryFilterLooseKey)
    );
  };
  const getCategoryDisciplineKey = (option = {}) => [
    option?.categoryFilter?.rfqType || option?.rfqType,
    option?.categoryFilter?.disciplineId || option?.disciplineId || option?.id,
  ].filter(Boolean).join("-");
  const selectedCategoryOption = categoryFilterOptions.find((option) =>
    isCategoryOptionSelected(option)
  );
  const projectOptions = projectWiseCounts
    .map((item) => ({
      name: item?._id,
      count: item?.count ?? 0,
    }))
    .filter((item) => item.name)
    .filter((item) =>
      item.name.toLowerCase().includes(projectSearch.trim().toLowerCase())
    );
  const normalizedCategorySearch = categorySearch.trim().toLowerCase();
  const matchesCategorySearch = (values) => {
    if (!normalizedCategorySearch) return true;
    return values.some((value) =>
      String(value || "").toLowerCase().includes(normalizedCategorySearch)
    );
  };
  const categoryHierarchyOptions = Object.values(
    categoryFilterOptions
      .filter((option) => (option?.count ?? 0) > 0)
      .reduce((acc, option) => {
      const categoryFilter = option?.categoryFilter || {};
      const disciplineId =
        categoryFilter.disciplineId || option?.disciplineId || option?.id;
      const disciplineLabel =
        option?.disciplineName ||
        categoryFilter.disciplineName ||
        (option?.type === "Discipline" ? option?.label : "");
      const rfqType = option?.rfqType || categoryFilter?.rfqType || "";
      const mainCategory =
        option?.mainCategory ||
        (rfqType ? rfqType.charAt(0).toUpperCase() + rfqType.slice(1) : "");

      if (!disciplineId || !disciplineLabel) return acc;

      const disciplineKey = [rfqType || "all", disciplineId].join(":");

      if (!acc[disciplineKey]) {
        acc[disciplineKey] = {
          key: disciplineKey,
          id: disciplineId,
          label: disciplineLabel,
          rfqType,
          mainCategory,
          count: 0,
          disciplineOption: null,
          children: [],
        };
      }

      if (!acc[disciplineKey].rfqType && rfqType) {
        acc[disciplineKey].rfqType = rfqType;
      }
      if (!acc[disciplineKey].mainCategory && mainCategory) {
        acc[disciplineKey].mainCategory = mainCategory;
      }

      if (option?.type === "Discipline") {
        acc[disciplineKey].count = option?.count ?? acc[disciplineKey].count;
        acc[disciplineKey].disciplineOption = option;
      } else {
        acc[disciplineKey].children.push(option);
      }

      return acc;
    }, {})
  )
    .map((discipline) => ({
      ...discipline,
      children: discipline.children
        .filter((option) =>
          matchesCategorySearch([
            discipline.label,
            option?.label,
            option?.path,
            option?.type,
            option?.rfqType,
            option?.mainCategory,
          ])
        )
        .sort((a, b) => String(a?.label || "").localeCompare(String(b?.label || ""))),
    }))
    .map((discipline) => ({
      ...discipline,
      count:
        discipline.count ||
        discipline.children.reduce((total, option) => total + (option?.count || 0), 0),
    }))
    .filter((discipline) =>
      discipline.count > 0 &&
      (matchesCategorySearch([discipline.label, discipline.mainCategory]) || discipline.children.length > 0)
    );
  const activeFilterCount = [
    rfqsData?.searchParam,
    rfqsData?.dateFilterIsActive,
    selectedStatus,
    selectedRfqTypes?.length,
    selectedBusinessDivisions?.length,
    selectedProjectNameList.length,
    selectedCategoryFilterKey,
    workspaceView === "Team Workspace" && selectedUserIds?.length,
  ].filter(Boolean).length;

  const getSelectedProjectNamesForRequest = (names = selectedProjectNameList) =>
    names.length > 0 ? names : undefined;

  const getSelectedCategoryForRequest = (category = selectedCategoryFilter) =>
    category || undefined;

  // --- Helper: compute userIds to send to backend based on current workspace and selections
  const getUserIdsForRequest = () => {
    // If Approved or Pending Approvals, use team + self (per earlier design)
    if (workspaceView === "Approved RFQs" || workspaceView === "Pending Approvals") {
      return [...(teamUserIds || []), user?.userId].filter(Boolean);
    }

    // Team Workspace: if no explicit selected users, send full team + self; otherwise send selected users
    if (workspaceView === "Team Workspace") {
      if (!selectedUserIds || selectedUserIds.length === 0) {
        return [...(teamUserIds || []), user?.userId].filter(Boolean);
      }
      return selectedUserIds.map(String);
    }

    // Default / My Workspace -> only current user
    return [String(user?.userId)];
  };

  const resetStatusFilter = () => {
    setSelectedStatus("");
    setRfqFilterStatus("");
  };

  const handleTeamInputChange = (e, userId) => {
    let userIds;
    if (e.target.checked) {
      userIds = [...selectedUserIds, userId];
    } else {
      userIds = selectedUserIds?.filter((id) => id != userId);
    }
    resetStatusFilter();
    setSelectedUserIds(userIds);
    handleChangeTeamMemberNew(userIds);
  };

  // Handle RFQ Type selection
  const handleRfqTypeChange = (e, rfqType) => {
    let newSelectedTypes;
    if (e.target.checked) {
      newSelectedTypes = [...selectedRfqTypes, rfqType];
    } else {
      newSelectedTypes = selectedRfqTypes?.filter((type) => type !== rfqType);
    }
    resetStatusFilter();
    setSelectedRfqTypes(newSelectedTypes);
    applyRfqTypeFilter(newSelectedTypes);
  };

  const applyRfqTypeFilter = (rfqTypesArr) => {
    const workspaceType = getWorkspaceType(workspaceView);
    const userIdsToSend = getUserIdsForRequest();

    if (workspaceView === "Approved RFQs") {
      // Use getApprovedRfqs for approved-by-me flow - note approvedByUserId passed below
      dispatch(
        getApprovedRfqs({
          entityId,
          dateFilterIsActive: rfqsData?.dateFilterIsActive,
          projectId,
          projectNames: getSelectedProjectNamesForRequest(),
          categoryFilter: getSelectedCategoryForRequest(),
          startDate: rfqsData?.startDate,
          endDate: rfqsData?.endDate,
          searchParam: rfqsData?.searchParam,
          status: "", // keep as blank for approved endpoint (it filters internally)
          userIds: userIdsToSend,
          closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
          startDateOfClosing: rfqsData?.startDateOfClosing,
          endDateOfClosing: rfqsData?.endDateOfClosing,
          rfqTypes: rfqTypesArr.length > 0 ? rfqTypesArr : undefined,
           businessDivision:
          selectedBusinessDivisions.length > 0
          ? selectedBusinessDivisions
          : undefined,
          approvedByUserId: user?.userId,
          workspaceType,
          approverId: user?.userId,
          IAM_role: user?.IAM_role,
        })
      );
      return;
    }

    // default: searchRfqWithDate
    dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status: "",
        userIds: userIdsToSend,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: rfqTypesArr.length > 0 ? rfqTypesArr : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const handleChangeStatus = (status) => {
    setSelectedStatus(status);
    setRfqFilterStatus(status); // pass to parent
    const workspaceType = getWorkspaceType(workspaceView);
    const userIdsToSend = getUserIdsForRequest();

    dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        searchParam: rfqsData?.searchParam,
        status,
        userIds: userIdsToSend,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const handleBusinessDivisionChange = (e, division) => {
  let updatedDivisions;

  if (e.target.checked) {
    updatedDivisions = [...selectedBusinessDivisions, division];
  } else {
    updatedDivisions = selectedBusinessDivisions.filter(
      (item) => item !== division
    );
  }

  resetStatusFilter();
  setSelectedBusinessDivisions(updatedDivisions);
  dispatch(
    searchRfqWithDate({
      projectId,
      projectNames: getSelectedProjectNamesForRequest(),
      categoryFilter: getSelectedCategoryForRequest(),
      entityId,
      dateFilterIsActive: rfqsData?.dateFilterIsActive,
      startDate: rfqsData?.startDate,
      endDate: rfqsData?.endDate,
      searchParam: rfqsData?.searchParam,
      status: "",
      userIds: getUserIdsForRequest(),
      closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
      startDateOfClosing: rfqsData?.startDateOfClosing,
      endDateOfClosing: rfqsData?.endDateOfClosing,
      rfqTypes:
        selectedRfqTypes.length > 0
          ? selectedRfqTypes
          : undefined,
      businessDivision: updatedDivisions,
      workspaceType: getWorkspaceType(workspaceView),
      approverId: user?.userId,
      IAM_role: user?.IAM_role,
      teamUserIds: teamUserIdsInString,
    })
  );
};

  const highlightedStatus = selectedStatus ?? "";

  const handleToggle = (key) => {
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDateChange = ({ startDate, endDate }, filterLabel) => {
    const workspaceType = getWorkspaceType(workspaceView);
    setDateFilterName(filterLabel || formatDateRangeLabel(startDate, endDate));
    setCreatedDateError("");
    dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        dateFilterIsActive: true,
        startDate,
        endDate,
        searchParam: rfqsData?.searchParam,
        status: selectedStatus !== undefined && selectedStatus !== null ? selectedStatus : rfqsData?.filterStatus,
        userIds: getUserIdsForRequest(),
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const handleCreatedDateOptionChange = (option) => {
    setSelectedCreatedDateOption(option);
    setCreatedDateError("");

    if (option === "custom") {
      return;
    }

    const range = getPresetDateRange(option);
    setCustomCreatedStartDate(range?.startDate || "");
    setCustomCreatedEndDate(range?.endDate || "");
    handleDateChange(range, formatDateRangeLabel(range?.startDate, range?.endDate));
  };

  const handleApplyCustomCreatedDate = () => {
    if (!customCreatedStartDate || !customCreatedEndDate) {
      setCreatedDateError("Please select both start and end date.");
      return;
    }

    if (new Date(customCreatedEndDate) < new Date(customCreatedStartDate)) {
      setCreatedDateError("End date cannot be before start date.");
      return;
    }

    handleDateChange(
      {
        startDate: customCreatedStartDate,
        endDate: customCreatedEndDate,
      },
      formatDateRangeLabel(customCreatedStartDate, customCreatedEndDate)
    );
  };

  const handleCloseDateChange = ({ startDate, endDate }) => {
    const workspaceType = getWorkspaceType(workspaceView);
    dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        closeDateFilterIsActive: true,
        startDateOfClosing: startDate,
        endDateOfClosing: endDate,
        searchParam: rfqsData?.searchParam,
        status: selectedStatus !== undefined && selectedStatus !== null ? selectedStatus : rfqsData?.filterStatus,
        userIds: getUserIdsForRequest(),
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const handleCloseDateClr = async () => {
    const workspaceType = getWorkspaceType(workspaceView);
    await dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        closeDateFilterIsActive: false,
        startDateOfClosing: "",
        endDateOfClosing: "",
        searchParam: rfqsData?.searchParam,
        status: selectedStatus !== undefined && selectedStatus !== null ? selectedStatus : rfqsData?.filterStatus,
        userIds: getUserIdsForRequest(),
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const handleClrSearch = async () => {
    const workspaceType = getWorkspaceType(workspaceView);
    await dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        closeDateFilterIsActive: false,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        searchParam: "",
        status: selectedStatus !== undefined && selectedStatus !== null ? selectedStatus : rfqsData?.filterStatus,
        userIds: getUserIdsForRequest(),
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
    // Do NOT clear selectedStatus here - keep user's selected status intact
  };

  const handleDateClr = async () => {
    const workspaceType = getWorkspaceType(workspaceView);
    await dispatch(
      searchRfqWithDate({
        projectId,
        projectNames: getSelectedProjectNamesForRequest(),
        categoryFilter: getSelectedCategoryForRequest(),
        entityId,
        dateFilterIsActive: false,
        startDate: "",
        endDate: "",
        searchParam: rfqsData?.searchParam,
        status: selectedStatus !== undefined && selectedStatus !== null ? selectedStatus : rfqsData?.filterStatus,
        userIds: getUserIdsForRequest(),
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
         businessDivision:
      selectedBusinessDivisions.length > 0
        ? selectedBusinessDivisions
        : undefined,
        workspaceType,
        approverId: user?.userId,
        IAM_role: user?.IAM_role,
        teamUserIds: teamUserIdsInString
      })
    );
  };

  const runRfqFilterSearch = (overrides = {}) => {
    resetStatusFilter();

    const payload = {
      projectId,
      projectNames: getSelectedProjectNamesForRequest(overrides.projectNames || selectedProjectNameList),
      categoryFilter: getSelectedCategoryForRequest(
        Object.prototype.hasOwnProperty.call(overrides, "categoryFilter")
          ? overrides.categoryFilter
          : selectedCategoryFilter
      ),
      entityId,
      dateFilterIsActive: rfqsData?.dateFilterIsActive,
      startDate: rfqsData?.startDate,
      endDate: rfqsData?.endDate,
      searchParam: rfqsData?.searchParam,
      status: "",
      userIds: getUserIdsForRequest(),
      closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
      startDateOfClosing: rfqsData?.startDateOfClosing,
      endDateOfClosing: rfqsData?.endDateOfClosing,
      rfqTypes: selectedRfqTypes.length > 0 ? selectedRfqTypes : undefined,
      businessDivision:
        selectedBusinessDivisions.length > 0
          ? selectedBusinessDivisions
          : undefined,
      workspaceType: getWorkspaceType(workspaceView),
      approverId: user?.userId,
      IAM_role: user?.IAM_role,
      teamUserIds: teamUserIdsInString,
      ...overrides,
    };

    dispatch(
      workspaceView === "Approved RFQs"
        ? getApprovedRfqs({
          ...payload,
          status: "",
          approvedByUserId: user?.userId,
        })
        : searchRfqWithDate(payload)
    );
  };

  const handleProjectChange = (projectName) => {
    const nextProjectNames = selectedProjectNameList.includes(projectName)
      ? selectedProjectNameList.filter((name) => name !== projectName)
      : [...selectedProjectNameList, projectName];

    setSelectedProjectNames(nextProjectNames);
    runRfqFilterSearch({ projectNames: nextProjectNames });
  };

  const handleCategoryFilterSelect = (option) => {
    const optionFilter = option?.categoryFilter || null;
    const isSelected = isCategoryOptionSelected(option);
    const nextCategoryFilter = isSelected ? null : optionFilter;

    if (option?.type === "Discipline" && !isSelected) {
      const disciplineKey = getCategoryDisciplineKey(option);
      setExpandedCategoryDisciplines((prev) => ({
        ...prev,
        [disciplineKey]: true,
      }));
    }

    setSelectedCategoryFilter(nextCategoryFilter);
    runRfqFilterSearch({ categoryFilter: nextCategoryFilter });
  };

  const toggleCategoryDiscipline = (disciplineKey) => {
    setExpandedCategoryDisciplines((prev) => ({
      ...prev,
      [disciplineKey]: !prev[disciplineKey],
    }));
  };

  const clearProjectFilter = (projectName) => {
    const nextProjectNames = selectedProjectNameList.filter((name) => name !== projectName);
    setSelectedProjectNames(nextProjectNames);
    runRfqFilterSearch({ projectNames: nextProjectNames });
  };

  const clearCategoryFilter = () => {
    setSelectedCategoryFilter(null);
    runRfqFilterSearch({ categoryFilter: null });
  };

  const handleClearAllFilters = () => {
    setSelectedStatus("");
    setRfqFilterStatus("");
    setSelectedRfqTypes([]);
    setSelectedBusinessDivisions([]);
    setSelectedProjectNames([]);
    setSelectedCategoryFilter(null);
    setSelectedCreatedDateOption("");
    setCustomCreatedStartDate("");
    setCustomCreatedEndDate("");
    setDateFilterName("");

    const payload = {
      projectId,
      entityId,
      dateFilterIsActive: false,
      startDate: "",
      endDate: "",
      searchParam: "",
      status: "",
      userIds: getUserIdsForRequest(),
      closeDateFilterIsActive: false,
      startDateOfClosing: "",
      endDateOfClosing: "",
      rfqTypes: undefined,
      categoryFilter: undefined,
      projectNames: undefined,
      businessDivision: undefined,
      workspaceType: getWorkspaceType(workspaceView),
      approverId: user?.userId,
      IAM_role: user?.IAM_role,
      teamUserIds: teamUserIdsInString,
    };

    dispatch(
      workspaceView === "Approved RFQs"
        ? getApprovedRfqs({
          ...payload,
          approvedByUserId: user?.userId,
        })
        : searchRfqWithDate(payload)
    );
  };

  useEffect(() => {
    if (workspaceView === "Team Workspace") {
      setSelectedUserIds([]); // default: none selected => backend will use team + self
    } else {
      setSelectedUserIds([user?.userId]); // default to self for My Workspace / others
    }
  }, [workspaceView, user?.userId]);

  useEffect(() => {
    setSelectedStatus(rfqFilterStatus);
  }, [rfqFilterStatus]);

  useEffect(() => {
    if (workspaceView !== "Team Workspace") {
      setTeamCreatorWiseCounts([]);
      return;
    }

    if (!Array.isArray(creatorWiseCounts) || creatorWiseCounts.length === 0) {
      return;
    }

    // Preserve the full-team count set. Once a team member is selected, the
    // backend response is intentionally narrowed to that creator, but the
    // filter labels should keep showing counts for every teammate.
    if (!selectedUserIds?.length) {
      setTeamCreatorWiseCounts(creatorWiseCounts);
    }
  }, [creatorWiseCounts, selectedUserIds?.length, workspaceView]);

  useEffect(() => {
    if (!rfqsData?.dateFilterIsActive || !rfqsData?.startDate || !rfqsData?.endDate) {
      setSelectedCreatedDateOption("");
      setCustomCreatedStartDate("");
      setCustomCreatedEndDate("");
      setCreatedDateError("");
      if (!rfqsData?.dateFilterIsActive) {
        setDateFilterName("");
      }
      return;
    }

    const matchedPreset = getMatchingDatePreset({
      startDate: rfqsData?.startDate,
      endDate: rfqsData?.endDate,
    });

    setSelectedCreatedDateOption(matchedPreset || "custom");
    setCustomCreatedStartDate(rfqsData?.startDate);
    setCustomCreatedEndDate(rfqsData?.endDate);
    setCreatedDateError("");
    setDateFilterName(
      formatDateRangeLabel(rfqsData?.startDate, rfqsData?.endDate)
    );
  }, [rfqsData?.dateFilterIsActive, rfqsData?.startDate, rfqsData?.endDate]);

  useEffect(() => {
    setAllDataClr(false);
  }, [filterApplied]);

  const FilterRow = ({
    children,
    count,
    control,
    disabled = false,
    hover = true,
  }) => (
    <label
      className={`flex items-center justify-between rounded-md px-1 py-1 text-xs ${
        disabled
          ? "text-gray-400"
          : `text-[#303026] ${hover ? "cursor-pointer hover:bg-[#FFF7F2]" : ""}`
      }`}
    >
      <span className="flex min-w-0 items-center gap-2">
        {control}
        <span className="truncate">{children}</span>
      </span>
      {count !== undefined && (
        <span className="ml-2 shrink-0 text-[11px] text-[#8A8A7A]">
          {count}
        </span>
      )}
    </label>
  );

  const SectionHeader = ({ id, title }) => (
    <button
      type="button"
      className="flex w-full items-center justify-between border-l-2 border-[#D65A22] bg-[#FFF7F2] px-3 py-2 text-left text-xs font-semibold text-[#8A3A1C]"
      onClick={() => handleToggle(id)}
    >
      <span>{title}</span>
      <svg
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
        className={`h-4 w-4 text-[#C8A99A] transition-transform ${openKeys[id] ? "rotate-180" : ""}`}
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );

  const chipClass =
    "inline-flex max-w-full items-center gap-1 rounded-full border border-[#F7B99D] bg-[#FFF4EE] px-2 py-[3px] text-[11px] font-medium text-[#B43A12]";
  const inputClass =
    "w-full rounded-lg border border-[#DDD8CD] px-3 py-2 text-xs outline-none focus:border-[#E95420] focus:ring-1 focus:ring-[#F8B090]";
  const checkboxClass =
    "h-4 w-4 rounded border-gray-300 text-[#D65A22] checked:border-[#D65A22] checked:bg-[#D65A22] focus:ring-[#D65A22]";
  const radioClass =
    "h-4 w-4 border-gray-300 text-[#D65A22] focus:ring-[#D65A22]";

  const hasActiveChips = !!rfqsData?.searchParam || !!rfqsData?.dateFilterIsActive || selectedProjectNameList.length > 0 || !!selectedCategoryOption;

  return (
    <aside className="h-full overflow-hidden rounded-xl border border-[#E8E8E0] bg-white shadow-sm">
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E8E8E0] bg-white px-3 py-2.5">
        <div>
          <p className="text-sm font-bold text-[#1F2937]">Filters</p>
          {hasActiveChips && (
            <p className="text-[11px] text-[#7A7067]">{activeFilterCount} applied</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasActiveChips && (
            <button
              type="button"
              onClick={handleClearAllFilters}
              className="text-xs font-semibold text-[#E95420] hover:text-[#C2410C]"
            >
              Clear All
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-[#7A7067] transition hover:bg-[#FFF4EE] hover:text-[#B43A12]"
              aria-label="Close filters"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5 5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <style>
        {`
          .rfq-filter-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .rfq-filter-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>

      <div className="rfq-filter-scrollbar h-[calc(100%-42px)] overflow-y-auto">
        {hasActiveChips && (
          <div className="sticky top-0 z-10 flex flex-wrap gap-1.5 border-b border-[#F0F0E8] bg-white p-2">
            {rfqsData?.searchParam && (
              <button type="button" className={chipClass} onClick={handleClrSearch}>
                Search: {rfqsData.searchParam} x
              </button>
            )}
            {rfqsData?.dateFilterIsActive && (
              <button type="button" className={chipClass} onClick={handleDateClr}>
                {dateFilterName || "Date filter"} x
              </button>
            )}
            {selectedProjectNameList.map((projectName) => (
              <button
                type="button"
                key={projectName}
                className={chipClass}
                onClick={() => clearProjectFilter(projectName)}
              >
                {projectName} x
              </button>
            ))}
            {selectedCategoryOption && (
              <button type="button" className={chipClass} onClick={clearCategoryFilter}>
                {selectedCategoryOption.label}
                {selectedCategoryOption.mainCategory ? ` (${selectedCategoryOption.mainCategory})` : ""} x
              </button>
            )}
          </div>
        )}

        <div className="bg-[#F4F8F8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8A8A7A]">
          RFQ Basics
        </div>

        <SectionHeader id="status" title="Status" />
        {openKeys.status && (
          <div className="space-y-0.5 px-3 py-2">
            <FilterRow
              count={totalCount}
              control={
              <input
                type="radio"
                name="rfqStatus"
                checked={highlightedStatus === ""}
                onChange={() => handleChangeStatus("")}
                className={radioClass}
              />
              }
            >
              All
            </FilterRow>
            {filterColData.map((status) => {
              const count = status === "OPEN" ? convertedData.OPEN ?? 0 : convertedData[status] ?? 0;
              return (
                <FilterRow
                  key={status}
                  count={count}
                  disabled={count === 0}
                  control={
                    <input
                      type="radio"
                      name="rfqStatus"
                      disabled={count === 0}
                      checked={highlightedStatus === status}
                      onChange={() => handleChangeStatus(status)}
                      className={radioClass}
                    />
                  }
                >
                  {status.charAt(0) + status.slice(1).toLowerCase()}
                </FilterRow>
              );
            })}
          </div>
        )}

         {businessDivisionOptions.length > 0 && (
          <>
            <SectionHeader id="businessDivision" title="Business Division" />
            {openKeys.businessDivision && (
              <div className="space-y-0.5 px-3 py-2">
                {businessDivisionOptions.map((division) => (
                  <FilterRow
                    key={division.value}
                    count={businessDivisionCountsMap[division.value] ?? 0}
                    control={
                      <input
                        type="checkbox"
                        checked={selectedBusinessDivisions.includes(division.value)}
                        onChange={(e) => handleBusinessDivisionChange(e, division.value)}
                        className={checkboxClass}
                      />
                    }
                  >
                    {division.label}
                  </FilterRow>
                ))}
              </div>
            )}
          </>
        )}

        <SectionHeader id="rfqType" title="RFQ Type" />
        {openKeys.rfqType && (
          <div className="space-y-0.5 px-3 py-2">
            {rfqTypes.map((type) => (
              <FilterRow
                key={type.value}
                count={rfqTypeCountsMap[type.value] ?? 0}
                control={
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={selectedRfqTypes.includes(type.value)}
                    onChange={(e) => handleRfqTypeChange(e, type.value)}
                  />
                }
              >
                {type.label}
              </FilterRow>
            ))}
          </div>
        )}

        {/* <SectionHeader id="category" title="Categories" />
        {openKeys.category && (
          <div className="space-y-2 px-3 py-2">
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search category or subcategory"
              className={inputClass}
            />
            <div className="rfq-filter-scrollbar max-h-64 space-y-2 overflow-y-auto pr-1">
              {categoryHierarchyOptions.length > 0 ? (
                categoryHierarchyOptions.map((discipline) => {
                  const disciplineOption = discipline.disciplineOption || {
                    id: discipline.id,
                    label: discipline.label,
                    type: "Discipline",
                    count: discipline.count,
                    path: discipline.label,
                    rfqType: discipline.rfqType,
                    mainCategory: discipline.mainCategory,
                    categoryFilter: {
                      disciplineId: discipline.id,
                      rfqType: discipline.rfqType,
                    },
                  };
                  const disciplineKey =
                    discipline.key || getCategoryDisciplineKey(disciplineOption);
                  const disciplineSelected = isCategoryOptionSelected(disciplineOption);
                  const isExpanded =
                    !!expandedCategoryDisciplines[disciplineKey] ||
                    disciplineSelected ||
                    !!normalizedCategorySearch;

                  return (
                    <div
                      key={disciplineKey}
                      className="overflow-hidden rounded-lg border border-[#EFE7DE] bg-white"
                    >
                      <div
                        className={`flex items-center justify-between gap-2 px-1 py-1 text-xs ${
                          disciplineSelected ? "bg-[#FFF4EE]" : "hover:bg-[#FFF7F2]"
                        }`}
                      >
                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={disciplineSelected}
                            onChange={() => handleCategoryFilterSelect(disciplineOption)}
                            className={checkboxClass}
                          />
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate">{discipline.label}</span>
                            {discipline.mainCategory && (
                              <span className="shrink-0 rounded-full bg-[#F2F2EA] px-1.5 py-0.5 text-[9px] font-semibold capitalize text-[#6B665A]">
                                {discipline.mainCategory}
                              </span>
                            )}
                          </span>
                        </label>
                        <div className="flex shrink-0 items-center gap-1">
                          <span className="text-[11px] text-[#8A8A7A]">
                            {discipline.count}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleCategoryDiscipline(disciplineKey)}
                            className="rounded p-1 text-[#8A8A7A] transition hover:bg-[#F2F2EA] hover:text-[#303026]"
                            aria-label={isExpanded ? "Hide categories" : "Show categories"}
                          >
                            <svg
                              aria-hidden="true"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {isExpanded && discipline.children.length > 0 && (
                        <div className="border-t border-[#F2E9E1] bg-[#FCFCFA] px-2 py-1">
                          {discipline.children.map((option) => {
                            const isSelected =
                              disciplineSelected ||
                              isCategoryOptionSelected(option);
                            return (
                              <FilterRow
                                key={getCategoryOptionKey(option) || `${option.type}-${option.id}`}
                                count={option.count}
                                control={
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleCategoryFilterSelect(option)}
                                    className={checkboxClass}
                                  />
                                }
                              >
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <span className="truncate">{option.label}</span>
                                  <span className="shrink-0 rounded-full bg-[#F2F2EA] px-1.5 py-0.5 text-[9px] text-[#6B665A]">
                                    {option.type}
                                  </span>
                                </span>
                              </FilterRow>
                            );
                          })}
                        </div>
                      )}

                      {isExpanded && discipline.children.length === 0 && (
                        <p className="border-t border-[#F2E9E1] bg-[#FCFCFA] px-3 py-2 text-xs text-[#8A8A7D]">
                          No categories found
                        </p>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="px-2 py-1 text-xs text-[#8A8A7D]">No categories found</p>
              )}
            </div>
          </div>
        )} */}

        <div className="bg-[#F4F8F8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8A8A7A]">
          Team & Date
        </div>

        <SectionHeader id="dateRange" title="Created Date" />
        {openKeys.dateRange && (
          <div className="space-y-0.5 px-3 py-2">
            {createdDateOptions.map((option) => (
              <FilterRow
                key={option.value}
                control={
                  <input
                    type="radio"
                    name="rfqCreatedDate"
                    checked={selectedCreatedDateOption === option.value}
                    onChange={() => handleCreatedDateOptionChange(option.value)}
                    className={radioClass}
                  />
                }
              >
                {option.label}
              </FilterRow>
            ))}
            {selectedCreatedDateOption === "custom" && (
              <div className="space-y-2 pt-2">
                <input
                  type="date"
                  value={customCreatedStartDate}
                  onChange={(e) => setCustomCreatedStartDate(e.target.value)}
                  className={inputClass}
                />
                <input
                  type="date"
                  value={customCreatedEndDate}
                  onChange={(e) => setCustomCreatedEndDate(e.target.value)}
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={handleApplyCustomCreatedDate}
                  className="w-full rounded-lg border border-[#1E1E08] px-3 py-2 text-xs font-semibold text-[#1E1E08]"
                >
                  Apply
                </button>
                {createdDateError && <p className="text-xs text-red-500">{createdDateError}</p>}
              </div>
            )}
          </div>
        )}

        {workspaceView === "Team Workspace" && (
          <>
            <SectionHeader id="team" title="Created By" />
            {openKeys.team && (
              <div className="space-y-0.5 px-3 py-2">
                <FilterRow
                  count={creatorCountsMap[String(user?.userId)] ?? 0}
                  control={
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={selectedUserIds?.includes(user?.userId)}
                      onChange={(e) => handleTeamInputChange(e, user?.userId)}
                    />
                  }
                >
                  {user?.name}
                </FilterRow>
                {user?.teamMembers?.map((data, index) => (
                  <FilterRow
                    key={data?.userId ?? index}
                    count={creatorCountsMap[String(data?.userId)] ?? 0}
                    control={
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={selectedUserIds?.includes(data?.userId)}
                        onChange={(e) => handleTeamInputChange(e, data?.userId)}
                      />
                    }
                  >
                    {data?.name}
                  </FilterRow>
                ))}
              </div>
            )}
          </>
        )}

         <SectionHeader id="project" title="Projects" />
        {openKeys.project && (
          <div className="space-y-2 px-3 py-2 mb-4">
            <input
              type="text"
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              placeholder="Search project name"
              className={inputClass}
            />
            <div className="rfq-filter-scrollbar max-h-52 space-y-0.5 overflow-y-auto pr-1">
              {projectOptions.length > 0 ? (
                projectOptions.map((project) => (
                  <FilterRow
                    key={project.name}
                    count={project.count}
                    control={
                      <input
                        type="checkbox"
                        checked={selectedProjectNameList.includes(project.name)}
                        onChange={() => handleProjectChange(project.name)}
                        className={checkboxClass}
                      />
                    }
                  >
                    {project.name}
                  </FilterRow>
                ))
              ) : (
                <p className="px-2 py-1 text-xs text-[#8A8A7D]">No projects found</p>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default FilterSection;
